"use client";

import { useSyncExternalStore } from "react";

// Real (non-debug) capability gate for /customize's desktop grid -- whether more than one tile is
// allowed a live, concurrent WebGL context, or the collage stays down to a single live tile regardless
// of screen size. See customize-preview-gallery.tsx's own mountLive comment for the full history: this
// replaced navigator.hardwareConcurrency/deviceMemory (directly falsified by a real device -- an 8-core
// budget Android tablet measured far worse, via actual FPS, than a 4-core iPhone) and sits ahead of the
// real FPS measurement (FpsProbe, same file) specifically for speed -- this resolves in single-digit
// milliseconds, not the ~1-2 real seconds FPS sampling needs, so every tile's live/static decision can
// be made essentially immediately rather than the other tiles visibly upgrading a couple seconds after
// the page settles. FpsProbe still runs (for the debug comparison view, DeviceCapabilityDebug) but no
// longer drives this decision.
//
// Configurable, not hardcoded inline, per explicit request -- 16384 is the ceiling nearly every
// phone/tablet/desktop shipped in the last several years reports; a device capped at 8192 or below is
// meaningfully more likely to be either genuinely old hardware or a lower-tier mobile GPU. Confirmed
// directly against real hardware: a real low-spec Android tablet read 8192, a real iPhone and a real
// desktop both read 16384 -- the one signal, out of everything tried, that actually separated them.
//
// This is NOT a claim that maxTextureSize tracks sustained rendering throughput precisely -- it's a
// coarse, few-discrete-values ceiling, and most non-ancient devices already max out at 16384 regardless
// of real GPU power, so clearing this threshold isn't a guarantee of smooth 4-way concurrency, only that
// the device isn't OBVIOUSLY too weak to bother trying.
export const MIN_CONCURRENT_MAX_TEXTURE_SIZE = 16384;

export function checkConcurrencyCapableByMaxTextureSize(threshold: number = MIN_CONCURRENT_MAX_TEXTURE_SIZE): boolean {
  const canvas = document.createElement("canvas");
  const gl = (canvas.getContext("webgl") ?? canvas.getContext("experimental-webgl")) as WebGLRenderingContext | null;
  if (!gl) return false; // no WebGL at all -- definitely not capable of MORE contexts
  const maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number;
  return maxTextureSize >= threshold;
}

// --- Site-wide singleton ---------------------------------------------------------------------------
// Explicit request: run this ONCE, as early as possible in the site's own load (WebglCapabilityPrimer,
// mounted in the root layout -- every page, not just /customize), rather than re-running it fresh each
// time a shopper happens to land on /customize. A plain module-level value, not React state -- it needs
// to survive across every client-side navigation for the tab's whole lifetime, which a value scoped to
// one page's component tree can't do (that tree unmounts/remounts as routes change; the root layout, and
// so this module, doesn't). useConcurrencyCapable subscribes any component that wants the result via
// useSyncExternalStore -- the correct React 18+ pattern for external mutable state -- so a component
// still re-renders correctly with the resolved value even in the unlikely case it reads before the
// primer's effect has run (the check itself is single-digit milliseconds, so that window is tiny).
let concurrencyCapable: boolean | null = null;
let primed = false;
const listeners = new Set<() => void>();

export function primeConcurrencyCapability(threshold?: number) {
  if (primed) return; // idempotent -- safe to call from more than one place (belt-and-suspenders)
  primed = true;
  concurrencyCapable = checkConcurrencyCapableByMaxTextureSize(threshold);
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): boolean | null {
  return concurrencyCapable;
}

function getServerSnapshot(): boolean | null {
  return null; // never computed during SSR -- this needs a real browser WebGL context
}

export function useConcurrencyCapable(): boolean | null {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
